import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { FiChevronDown, FiChevronRight, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi"

import type { AxiosError as ApiError } from "axios"
import {
  BudgetCategorys,
  ProviderUserTypes,
  ServiceCatalog,
  type BudgetCategoryPublic,
  type BudgetSubCategoryPublic,
  type ProviderUserTypePublic,
  type ServiceItemPublic,
} from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"

export const Route = createFileRoute("/_layout/provider/providerusertypes")({
  component: ProviderUserTypesPage,
})

// ── Add/Edit dialog ───────────────────────────────────────────────────────────

const UserTypeDialog = ({
  providerId,
  existing,
  onClose,
}: {
  providerId: string
  existing?: ProviderUserTypePublic
  onClose: () => void
}) => {
  const [name, setName] = useState(existing?.name ?? "")
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const mutation = useMutation({
    mutationFn: () =>
      existing
        ? ProviderUserTypes.updateProviderUserType({ path: { id: existing.id }, body: { name } })
        : ProviderUserTypes.createProviderUserType({ body: { providerId, name } }),
    onSuccess: () => {
      showSuccessToast(existing ? "User type updated." : "User type created.")
      queryClient.invalidateQueries({ queryKey: ["providerUserTypes", providerId] })
      onClose()
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <VStack gap={4} align="stretch">
      <Field label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Support Worker, Nurse, Coordinator"
          onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) mutation.mutate() }}
        />
      </Field>
      <Flex justify="flex-end" gap={2} pt={2}>
        <Button variant="subtle" colorPalette="gray" onClick={onClose}>Cancel</Button>
        <Button loading={mutation.isPending} disabled={!name.trim()} onClick={() => mutation.mutate()}>
          {existing ? "Save" : "Create"}
        </Button>
      </Flex>
    </VStack>
  )
}

// ── Skill assignments panel ───────────────────────────────────────────────────

const SkillAssignmentsPanel = ({
  userType,
  providerId,
}: {
  userType: ProviderUserTypePublic
  providerId: string
}) => {
  const queryClient = useQueryClient()

  // Load all assigned service item IDs for this user type
  const { data: assignedData, isLoading: loadingAssigned } = useQuery({
    queryKey: ["userTypeItems", userType.id],
    queryFn: () => ServiceCatalog.listItemsForUserType({ path: { providerUserTypeId: userType.id } }),
  })
  const assignedIds = new Set((assignedData ?? []).map((r: any) => r.serviceItemId))

  // Load all service subcategories (service type only) for this provider
  const { data: catsData } = useQuery({
    queryKey: ["budgetCategories"],
    queryFn: () => BudgetCategorys.readBudgetCategorys(),
  })
  const categories: BudgetCategoryPublic[] = catsData?.data ?? []

  const subQueries = useQueries({
    queries: categories.map((cat) => ({
      queryKey: ["budgetSubCategories", providerId, cat.id, "service"],
      queryFn: () =>
        ServiceCatalog.listSubcategoriesByProvider({
          path: { providerId },
          query: { budgetCategoryId: cat.id, subCategoryType: "service" },
        }),
      enabled: !!cat.id,
    })),
  })

  const allSubs: BudgetSubCategoryPublic[] = subQueries.flatMap((q) => q.data?.data ?? [])

  const itemQueries = useQueries({
    queries: allSubs.map((sub) => ({
      queryKey: ["serviceItems", sub.id],
      queryFn: () => ServiceCatalog.listItemsBySubcategory({ path: { subCategoryId: sub.id } }),
      enabled: !!sub.id,
    })),
  })

  const subItemMap = new Map<string, ServiceItemPublic[]>()
  allSubs.forEach((sub, i) => {
    subItemMap.set(sub.id, itemQueries[i]?.data?.data ?? [])
  })

  const addMutation = useMutation({
    mutationFn: (serviceItemId: string) =>
      ServiceCatalog.addItemUserType({ path: { serviceItemId, providerUserTypeId: userType.id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["userTypeItems", userType.id] }),
    onError: (err: ApiError) => handleError(err),
  })

  const removeMutation = useMutation({
    mutationFn: (serviceItemId: string) =>
      ServiceCatalog.removeItemUserType({ path: { serviceItemId, providerUserTypeId: userType.id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["userTypeItems", userType.id] }),
    onError: (err: ApiError) => handleError(err),
  })

  const isPending = addMutation.isPending || removeMutation.isPending
  const isLoading = loadingAssigned || subQueries.some((q) => q.isLoading) || itemQueries.some((q) => q.isLoading)

  const handleToggle = (serviceItemId: string, currentlyAssigned: boolean) => {
    if (currentlyAssigned) removeMutation.mutate(serviceItemId)
    else addMutation.mutate(serviceItemId)
  }

  // Build display: only categories that have service subs with items
  const categoryDisplay = categories.map((cat, catIdx) => {
    const subs = subQueries[catIdx]?.data?.data ?? []
    return {
      cat,
      subs: subs.filter((sub) => (subItemMap.get(sub.id) ?? []).length > 0),
    }
  }).filter((g) => g.subs.length > 0)

  if (isLoading) return <Spinner size="sm" m={3} />

  if (categoryDisplay.length === 0) {
    return (
      <Box px={4} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
        <Text fontSize="sm" color="gray.400">No service items found. Add items in the Service Catalog first.</Text>
      </Box>
    )
  }

  return (
    <Box px={4} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="wide">
        Allowed Service Items
        <Badge ml={2} size="xs" colorPalette="blue">{assignedIds.size} assigned</Badge>
      </Text>

      <VStack align="stretch" gap={4}>
        {categoryDisplay.map(({ cat, subs }) => (
          <Box key={cat.id}>
            <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2} textTransform="uppercase">
              {cat.category}
            </Text>
            {subs.map((sub) => {
              const items = subItemMap.get(sub.id) ?? []
              return (
                <Box key={sub.id} mb={3} pl={2}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                    {sub.name}
                  </Text>
                  <Flex gap={3} flexWrap="wrap">
                    {items.map((item) => {
                      const assigned = assignedIds.has(item.id)
                      return (
                        <Flex
                          key={item.id}
                          align="center"
                          gap={2}
                          p={2}
                          borderRadius="md"
                          bg={assigned ? "blue.50" : "white"}
                          borderWidth="1px"
                          borderColor={assigned ? "blue.200" : "gray.200"}
                          opacity={isPending ? 0.7 : 1}
                        >
                          <Checkbox.Root
                            checked={assigned}
                            disabled={isPending}
                            onCheckedChange={() => handleToggle(item.id, assigned)}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                            <Checkbox.Label fontSize="sm">{item.name}</Checkbox.Label>
                          </Checkbox.Root>
                        </Flex>
                      )
                    })}
                  </Flex>
                </Box>
              )
            })}
          </Box>
        ))}
      </VStack>
    </Box>
  )
}

// ── User type row ─────────────────────────────────────────────────────────────

const UserTypeRow = ({
  userType,
  providerId,
}: {
  userType: ProviderUserTypePublic
  providerId: string
}) => {
  const [editOpen, setEditOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const { data: assignedData } = useQuery({
    queryKey: ["userTypeItems", userType.id],
    queryFn: () => ServiceCatalog.listItemsForUserType({ path: { providerUserTypeId: userType.id } }),
  })
  const assignedCount = assignedData?.length ?? 0

  const deleteMutation = useMutation({
    mutationFn: () => ProviderUserTypes.deleteProviderUserType({ path: { id: userType.id } }),
    onSuccess: () => {
      showSuccessToast("User type deleted.")
      queryClient.invalidateQueries({ queryKey: ["providerUserTypes", providerId] })
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <Flex
        align="center"
        gap={2}
        px={4}
        py={3}
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box color="gray.400" flexShrink={0}>
          {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        </Box>
        <Text fontWeight="medium" flex={1}>{userType.name}</Text>
        <Badge size="sm" colorPalette={assignedCount > 0 ? "blue" : "gray"}>
          {assignedCount} service item{assignedCount !== 1 ? "s" : ""}
        </Badge>
        <Flex gap={1} onClick={(e) => e.stopPropagation()}>
          <IconButton
            aria-label="Edit"
            size="xs"
            variant="ghost"
            onClick={() => setEditOpen(true)}
          >
            <FiEdit2 />
          </IconButton>
          <IconButton
            aria-label="Delete"
            size="xs"
            variant="ghost"
            colorPalette="red"
            loading={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            <FiTrash2 />
          </IconButton>
        </Flex>
      </Flex>

      {expanded && <SkillAssignmentsPanel userType={userType} providerId={providerId} />}

      <DialogRoot
        open={editOpen}
        onOpenChange={({ open }) => setEditOpen(open)}
        size={{ base: "xs", md: "sm" }}
        placement="center"
      >
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User Type</DialogTitle></DialogHeader>
          <DialogBody>
            <UserTypeDialog
              providerId={providerId}
              existing={userType}
              onClose={() => setEditOpen(false)}
            />
          </DialogBody>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function ProviderUserTypesPage() {
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""
  const [createOpen, setCreateOpen] = useState(false)

  const { data: typesData, isLoading } = useQuery({
    queryKey: ["providerUserTypes", providerId],
    queryFn: () => ProviderUserTypes.listProviderUserTypes({ query: { providerId } }),
    enabled: !!providerId,
  })
  const userTypes = typesData?.data ?? []

  return (
    <Container maxW="full">
      <Heading size="lg" pt={12} mb={2}>Provider User Types</Heading>
      <Text color="gray.500" fontSize="sm" mb={6}>
        Define roles for provider users (e.g. Support Worker, Nurse). Click a role to assign which service items that role can deliver.
      </Text>

      <Flex mb={4}>
        <DialogRoot
          open={createOpen}
          onOpenChange={({ open }) => setCreateOpen(open)}
          size={{ base: "xs", md: "sm" }}
          placement="center"
        >
          <DialogTrigger asChild>
            <Button size="sm"><FiPlus /> New User Type</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Provider User Type</DialogTitle></DialogHeader>
            <DialogBody>
              <UserTypeDialog providerId={providerId} onClose={() => setCreateOpen(false)} />
            </DialogBody>
            <DialogCloseTrigger />
          </DialogContent>
        </DialogRoot>
      </Flex>

      {isLoading && <Spinner />}

      {!isLoading && userTypes.length === 0 && (
        <Text color="gray.400">No user types yet. Create one to get started.</Text>
      )}

      <VStack gap={2} align="stretch">
        {userTypes.map((ut) => (
          <UserTypeRow key={ut.id} userType={ut} providerId={providerId} />
        ))}
      </VStack>
    </Container>
  )
}
